"use client";

import { CommonIcon } from "../common-icon/common-icon";
import React, { useEffect, useState } from "react";

interface ToastProps {
  message: string;
  offset?: number;
  duration?: number;
  onClose?: () => void;
  type?: "success" | "error";
}

type ToastListener = (toast: ToastProps & { id: number }) => void;

let toastKey = 0;
const listeners = new Set<ToastListener>();

const ToastContent: React.FC<ToastProps & { visible: boolean }> = ({
  message,
  offset = 20,
  visible,
  type = "success",
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => {
      setMounted(true);
    });
  }, []);

  const isError = type === "error";

  return (
    <div
      className={[
        "fixed left-1/2 z-50 -translate-x-1/2 transition-all duration-300 ease-out",
        mounted && visible
          ? "translate-y-0 opacity-100"
          : "-translate-y-12 opacity-0",
      ].join(" ")}
      style={{ top: offset }}
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
    >
      <div
        className={[
          "mx-auto max-w-[calc(100vw-80px)]",
          isError
            ? "w-[min(560px,calc(100vw-40px))] rounded-[8px] border border-[rgba(255,8,8,0.15)] bg-[#fff0f0] px-3 py-5"
            : "w-max rounded-lg bg-black/75 px-4 py-3 text-white",
        ].join(" ")}
      >
        <div
          className={
            isError ? "flex items-center gap-4" : "flex items-start gap-2"
          }
        >
          {isError ? (
            <div className="h-6 w-6 shrink-0">
              <CommonIcon
                iconName="WarningCircle"
                className="h-6 w-6"
                iconColor="#ff5150"
              />
            </div>
          ) : null}
          <span
            className={
              isError
                ? "break-words text-[14px] font-normal leading-6 text-[#ff5150]"
                : "break-words text-[14px] font-medium"
            }
          >
            {message}
          </span>
        </div>
      </div>
    </div>
  );
};

export const Toast = {
  show: (props: ToastProps) => {
    const toast = { ...props, id: toastKey++ };
    listeners.forEach((listener) => listener(toast));
  },
};

/**
 * 与 packages/api/src/regis-api/regis-api-proxy.ts 里的同名事件常量对应
 * （两边故意不共享 import，靠事件名字符串解耦：api 层广播，这里监听并弹 Toast）。
 * 后端响应 code 不是 "0000" 时，regis-api-proxy 会往 window 广播这个事件。
 */
const BUSINESS_ERROR_EVENT = "trex-regis-business-error";
const BUSINESS_ERROR_MESSAGE = "Something went wrong. Please try again later.";

if (typeof window !== "undefined") {
  window.addEventListener(BUSINESS_ERROR_EVENT, () => {
    Toast.show({ type: "error", message: BUSINESS_ERROR_MESSAGE });
  });
}

export function ToastViewport() {
  const [toast, setToast] = useState<(ToastProps & { id: number }) | null>(
    null,
  );
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handler: ToastListener = (nextToast) => {
      setToast(nextToast);
      setVisible(true);
    };
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, []);

  useEffect(() => {
    if (!toast || !visible) return;
    const timer = setTimeout(() => {
      setVisible(false);
    }, toast.duration || 3000);
    return () => clearTimeout(timer);
  }, [toast, visible]);

  useEffect(() => {
    if (!toast || visible) return;
    const timer = setTimeout(() => {
      toast.onClose?.();
      setToast(null);
    }, 300);
    return () => clearTimeout(timer);
  }, [toast, visible]);

  if (!toast) return null;
  return <ToastContent key={toast.id} {...toast} visible={visible} />;
}
